// Mock data for Naviton Global website

export const companyInfo = {
  name: "Naviton Global Pty Ltd",
  tagline: "Connecting Australian quality to global markets.",
  email: "info@navitonglobal.com.au",
  phone: "+61 478822581",
  address: "Sydney, Australia",
  abn: "31693199638"
};

export const heroData = {
  headline: "Global Trade. Trusted Import & Exports. Australian Quality.",
  subheadline: "Your strategic partner for international trade, premium sourcing, and seamless logistics coordination across global markets. We facilitate both import and export activities with precision and reliability.",
  tagline: "Proudly Australian | Serving businesses nationwide — Sydney • Melbourne • Brisbane • Perth • Adelaide",
  image: "https://images.unsplash.com/photo-1759272548470-d0686d071036?w=1920&q=80",
  stats: [
    { value: "Global", label: "Market Reach" },
    { value: "Asia-Pacific", label: "Trade Focus" },
    { value: "Growing", label: "Partner Network" },
    { value: "15+", label: "Years Experience" }
  ]
};

export const services = [
  {
    id: "domestic-trade",
    title: "Domestic Trade & Distribution",
    description: "Nationwide logistics and distribution solutions across Australia. We connect businesses from coast to coast with reliable interstate trade support.",
    icon: "MapPin",
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80",
    features: ["Interstate logistics", "Australian supplier network", "Local warehousing", "Nationwide delivery", "Regional partnerships"]
  },
  {
    id: "import-export",
    title: "Import & Export Services",
    description: "Comprehensive import and export solutions connecting Australian businesses with global markets. We manage the complete trade cycle from sourcing to delivery.",
    icon: "Globe",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
    features: ["Import facilitation", "Export documentation", "Trade compliance", "Market access", "Customs coordination"]
  },
  {
    id: "sourcing",
    title: "International Sourcing",
    description: "Access premium products from Australia and global markets through our extensive supplier network and quality-verified sourcing processes.",
    icon: "Search",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80",
    features: ["Supplier vetting", "Quality control", "Price negotiation", "Sample coordination"]
  },
  {
    id: "buyer-matching",
    title: "Buyer–Supplier Matching",
    description: "Connect with verified buyers and suppliers through our established international trade network spanning multiple continents.",
    icon: "Handshake",
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&q=80",
    features: ["Partner verification", "Trade facilitation", "Contract support", "Relationship management"]
  },
  {
    id: "logistics",
    title: "Logistics Coordination",
    description: "Comprehensive logistics management from warehouse to destination, ensuring timely and secure delivery for both imports and exports.",
    icon: "Truck",
    image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&q=80",
    features: ["Freight management", "Customs clearance", "Warehousing", "Tracking systems"]
  },
  {
    id: "documentation",
    title: "Documentation & Compliance",
    description: "Expert handling of all import and export documentation and regulatory compliance requirements for seamless international trade.",
    icon: "FileCheck",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
    features: ["Import permits", "Export licences", "Certificates of origin", "Customs documentation"]
  },
  {
    id: "market-entry",
    title: "Market Entry Support",
    description: "Strategic guidance for entering new international markets with reduced risk and accelerated growth, whether importing or exporting.",
    icon: "TrendingUp",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    features: ["Market research", "Regulatory guidance", "Partner introductions", "Entry strategy"]
  }
];

export const products = [
  {
    id: "agricultural",
    category: "Agricultural Products",
    description: "Premium agricultural products for import and export including grains, pulses, oilseeds, and specialty crops from Australia and global sources.",
    image: "https://images.unsplash.com/photo-1755931359594-852c73c6609c?w=800&q=80",
    items: ["Wheat & Grains", "Pulses & Lentils", "Oilseeds", "Cotton", "Sugar"],
    origin: "Global",
    exportReady: true,
    compliance: ["AQIS Certified", "Non-GMO Available", "Organic Options"]
  },
  {
    id: "timber",
    category: "Timber & Raw Materials",
    description: "Sustainably sourced timber and raw materials for construction and manufacturing, available for both import and export.",
    image: "https://images.unsplash.com/photo-1742376089169-338ec2542c4f?w=800&q=80",
    items: ["Hardwood Timber", "Softwood Products", "Wood Chips", "Processed Materials"],
    origin: "Global",
    exportReady: true,
    compliance: ["FSC Certified", "PEFC Available", "Fumigation Ready"]
  },
  {
    id: "fmcg",
    category: "FMCG & Packaged Goods",
    description: "Quality food and consumer products for international retail and distribution, facilitating both inbound and outbound trade.",
    image: "https://images.pexels.com/photos/19052801/pexels-photo-19052801.jpeg?w=800",
    items: ["Dairy Products", "Beverages", "Health Foods", "Skincare", "Supplements"],
    origin: "Global",
    exportReady: true,
    compliance: ["TGA Registered", "Halal Available", "FDA Compliant"]
  },
  {
    id: "custom",
    category: "Custom Sourcing",
    description: "Tailored sourcing solutions for specific product requirements, connecting you with suppliers and buyers worldwide.",
    image: "https://images.unsplash.com/photo-1758777625576-09fc43631902?w=800&q=80",
    items: ["Bespoke Products", "Private Label", "OEM Manufacturing", "Specialty Items"],
    origin: "Global",
    exportReady: true,
    compliance: ["Custom Certification", "Buyer Specifications", "Quality Assured"]
  }
];

export const markets = [
  {
    id: "india",
    name: "India",
    region: "South Asia",
    description: "Strategic gateway to the world's fastest-growing major economy. We facilitate both imports from and exports to India across multiple sectors.",
    highlights: ["Bilateral trade growth", "Strong manufacturing base", "Growing middle class"],
    tradeFocus: ["Pulses", "Timber", "Dairy", "Textiles", "Technology"]
  },
  {
    id: "southeast-asia",
    name: "Southeast Asia",
    region: "Asia Pacific",
    description: "Dynamic markets including Singapore, Malaysia, Indonesia, Vietnam, and Thailand. Key corridor for two-way trade with Australia.",
    highlights: ["RCEP member nations", "Rising consumption", "Manufacturing hubs"],
    tradeFocus: ["FMCG", "Agricultural", "Electronics", "Raw Materials"]
  },
  {
    id: "middle-east",
    name: "Middle East",
    region: "MENA",
    description: "Premium markets including UAE, Saudi Arabia, and the GCC region. Strong demand for quality imports and growing export capabilities.",
    highlights: ["Halal market access", "Premium positioning", "Food security focus"],
    tradeFocus: ["Meat", "Dairy", "Grains", "Consumer Goods"]
  },
  {
    id: "china",
    name: "China",
    region: "East Asia",
    description: "World's largest trading nation. We facilitate imports of manufactured goods and exports of premium Australian products.",
    highlights: ["ChAFTA benefits", "E-commerce growth", "Manufacturing leader"],
    tradeFocus: ["Dairy", "Wine", "Health products", "Electronics", "Machinery"]
  },
  {
    id: "japan-korea",
    name: "Japan & Korea",
    region: "East Asia",
    description: "Established markets with strong appreciation for quality. Long-standing trade relationships for both imports and exports.",
    highlights: ["FTA access", "Quality premium", "Long-term partnerships"],
    tradeFocus: ["Beef", "Seafood", "Timber", "Automotive Parts", "Technology"]
  },
  {
    id: "emerging",
    name: "Emerging Markets",
    region: "Global",
    description: "Expanding presence in Africa, South America, and Eastern Europe. New opportunities for importers and exporters alike.",
    highlights: ["Growth potential", "New partnerships", "Diversification"],
    tradeFocus: ["Agricultural", "Manufacturing inputs", "Consumer goods"]
  }
];

export const certifications = [
  {
    title: "Australian Trade Standards",
    description: "Full compliance with Australian government import and export regulations and quality standards.",
    icon: "ShieldCheck"
  },
  {
    title: "Fumigation & Quarantine",
    description: "AQIS-compliant treatment and certification for all applicable import and export products.",
    icon: "Bug"
  },
  {
    title: "Quality Assurance",
    description: "Rigorous quality control processes from sourcing to delivery for all trade activities.",
    icon: "Award"
  },
  {
    title: "Customs Documentation",
    description: "Complete and accurate documentation for smooth customs clearance on imports and exports.",
    icon: "FileText"
  },
  {
    title: "Trade Regulations",
    description: "Up-to-date compliance with international trade regulations, tariffs, and bilateral agreements.",
    icon: "Scale"
  },
  {
    title: "Environmental Standards",
    description: "Commitment to sustainable and environmentally responsible trade practices globally.",
    icon: "Leaf"
  }
];

export const partners = {
  types: [
    {
      category: "Buyers",
      description: "International importers and distributors across multiple continents.",
      count: "200+"
    },
    {
      category: "Suppliers",
      description: "Verified producers and manufacturers from Australia and worldwide.",
      count: "150+"
    },
    {
      category: "Logistics Partners",
      description: "Global freight forwarders and shipping lines.",
      count: "50+"
    },
    {
      category: "Trade Bodies",
      description: "Government and industry associations.",
      count: "20+"
    }
  ],
  featured: [
    { name: "Austrade", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" },
    { name: "DFAT", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" },
    { name: "Australian Chamber", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" },
    { name: "Export Council", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" },
    { name: "Trade Alliance", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" },
    { name: "Global Logistics", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Coat_of_Arms_of_Australia.svg/120px-Coat_of_Arms_of_Australia.svg.png" }
  ]
};

export const blogPosts = [
  {
    id: 1,
    title: "Import & Export from Australia: A Complete Guide for 2025",
    excerpt: "Everything you need to know about Australian import and export procedures, documentation, and compliance requirements.",
    category: "Trade Guide",
    date: "2025-08-01",
    readTime: "8 min read",
    link: "https://www.austrade.gov.au/australian/export/guide-to-exporting",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
  },
  {
    id: 2,
    title: "India-Australia Trade: Opportunities After ECTA",
    excerpt: "How the Economic Cooperation and Trade Agreement is reshaping bilateral trade opportunities for importers and exporters.",
    category: "Market Insights",
    date: "2025-07-25",
    readTime: "6 min read",
    link: "https://www.dfat.gov.au/trade/agreements/in-force/australia-india-ecta",
    image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=800&q=80"
  },
  {
    id: 3,
    title: "Global Supply Chain Trends Shaping 2025",
    excerpt: "Key supply chain developments and how they impact international trade flows to and from Australia.",
    category: "Industry Trends",
    date: "2025-07-18",
    readTime: "5 min read",
    link: "https://www.wto.org/english/res_e/publications_e/wtr23_e.htm",
    image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&q=80"
  },
  {
    id: 4,
    title: "Compliance Essentials for International Trade",
    excerpt: "Understanding import and export requirements, customs regulations, and quality certifications for global trade.",
    category: "Compliance",
    date: "2025-07-10",
    readTime: "7 min read",
    link: "https://www.abf.gov.au/importing-exporting-and-manufacturing",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
  }
];

export const fullBlogPosts = [
  {
    id: 1,
    slug: "australia-import-export-guide-2025",
    title: "The Complete Guide to Import and Export from Australia in 2025",
    excerpt: "A comprehensive overview of Australian trade procedures, documentation requirements, and best practices for international commerce.",
    category: "Trade Guide",
    date: "2025-08-01",
    readTime: "12 min read",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["Import", "Export", "Australia", "Trade Guide", "Compliance"],
    content: `
## Introduction to Australian International Trade

Australia's strategic position in the Asia-Pacific region makes it a vital hub for international trade. With robust trade agreements spanning over 15 countries and regions, Australian businesses have unprecedented access to global markets.

## Understanding Import Procedures

### Documentation Requirements
- Commercial Invoice
- Packing List
- Bill of Lading or Airway Bill
- Certificate of Origin
- Import Permits (where applicable)

### Customs Clearance Process
The Australian Border Force (ABF) oversees all import activities. Key steps include:
1. Lodging an import declaration
2. Payment of duties and taxes
3. Biosecurity inspection (if required)
4. Release of goods

## Export Procedures Explained

### Export Documentation
Exporting from Australia requires careful attention to documentation:
- Export Declaration
- Commercial Invoice
- Phytosanitary Certificate (for agricultural products)
- Certificate of Origin
- Letter of Credit (if applicable)

### Compliance Requirements
Australian exporters must comply with:
- Customs Act 1901
- Export Control Act 2020
- Biosecurity Act 2015

## Key Trade Agreements

Australia has established free trade agreements with:
- China (ChAFTA)
- Japan (JAEPA)
- Korea (KAFTA)
- India (ECTA)
- ASEAN nations (AANZFTA)
- United Kingdom (A-UKFTA)

## Tips for Success

1. **Partner with experienced trade professionals** - Navigate complex regulations with expert guidance
2. **Understand your target market** - Research cultural and regulatory requirements
3. **Ensure documentation accuracy** - Avoid costly delays with proper paperwork
4. **Build strong relationships** - Long-term partnerships drive sustainable growth

## Conclusion

International trade offers tremendous opportunities for Australian businesses. With proper planning, documentation, and compliance, companies can successfully navigate the complexities of global commerce.
    `
  },
  {
    id: 2,
    slug: "india-australia-trade-ecta-opportunities",
    title: "India-Australia ECTA: Unlocking New Trade Opportunities in 2025",
    excerpt: "Explore how the Economic Cooperation and Trade Agreement is transforming bilateral trade between India and Australia.",
    category: "Market Insights",
    date: "2025-07-25",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["India", "ECTA", "Free Trade", "Market Access", "Bilateral Trade"],
    content: `
## The India-Australia Economic Cooperation and Trade Agreement

The ECTA, which came into force in December 2022, represents a landmark achievement in bilateral trade relations. This agreement has opened significant opportunities for businesses in both nations.

## Key Benefits for Australian Exporters

### Tariff Reductions
- Immediate elimination of tariffs on over 85% of Australian goods
- Progressive reduction on remaining tariff lines
- Zero duty access for wine, wool, and seafood

### Market Access Improvements
- Enhanced access to India's 1.4 billion consumer market
- Reduced barriers for services sectors
- Improved investment protections

## Opportunities for Indian Exporters to Australia

### Growing Sectors
- Textiles and apparel
- Pharmaceuticals
- Information technology services
- Agricultural products

### Competitive Advantages
- Skilled workforce
- Cost-effective manufacturing
- Growing quality standards

## Trade Statistics

Since ECTA implementation:
- Bilateral trade has increased by 25%
- Australian wine exports to India up 40%
- Indian IT services to Australia growing steadily

## Strategic Sectors

### Agriculture
Australian wheat, pulses, and lentils find strong demand in India's growing food processing industry.

### Resources
India's infrastructure development creates demand for Australian minerals and resources.

### Education and Services
Enhanced mobility provisions benefit both nations' service sectors.

## How Naviton Global Can Help

We specialise in facilitating India-Australia trade with:
- Market entry strategies
- Compliance guidance
- Partner identification
- Logistics coordination

## Looking Ahead

The ECTA is just the beginning. Both nations are working toward a Comprehensive Economic Cooperation Agreement (CECA) that will further expand opportunities.
    `
  },
  {
    id: 3,
    slug: "global-supply-chain-trends-2025",
    title: "5 Global Supply Chain Trends Reshaping International Trade in 2025",
    excerpt: "From digitalisation to sustainability, discover the key trends transforming how goods move across borders.",
    category: "Industry Trends",
    date: "2025-07-18",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["Supply Chain", "Logistics", "Technology", "Sustainability", "Trade"],
    content: `
## The Evolution of Global Supply Chains

The past few years have fundamentally changed how businesses approach supply chain management. Here are the five trends shaping 2025.

## 1. Digital Transformation

### Blockchain in Trade
- Enhanced transparency and traceability
- Reduced paperwork and fraud
- Faster customs clearance

### AI and Automation
- Predictive demand forecasting
- Automated documentation
- Smart inventory management

## 2. Supply Chain Resilience

### Diversification Strategies
Post-pandemic, businesses are:
- Reducing single-source dependencies
- Building regional supply networks
- Maintaining strategic inventory buffers

### Risk Management
- Real-time monitoring systems
- Scenario planning capabilities
- Alternative supplier networks

## 3. Sustainability Focus

### Green Logistics
- Carbon-neutral shipping options
- Sustainable packaging requirements
- Environmental compliance tracking

### ESG Requirements
- Scope 3 emissions reporting
- Ethical sourcing verification
- Circular economy integration

## 4. Nearshoring and Regionalization

### Shifting Patterns
- Manufacturing moving closer to markets
- Regional trade agreements gaining importance
- Reduced reliance on distant suppliers

### Australia's Opportunity
- Gateway to Asia-Pacific markets
- Growing manufacturing capabilities
- Strong regulatory environment

## 5. Enhanced Visibility

### End-to-End Tracking
- IoT sensors throughout the chain
- Real-time shipment monitoring
- Predictive arrival times

### Data Analytics
- Performance benchmarking
- Cost optimisation insights
- Customer experience improvements

## Implications for Australian Traders

These trends present both challenges and opportunities:
- Invest in digital capabilities
- Build diverse supplier relationships
- Prioritise sustainability
- Enhance visibility and communication

## Conclusion

Adaptability and forward-thinking strategies will define success in the evolving global trade landscape.
    `
  },
  {
    id: 4,
    slug: "trade-compliance-best-practices",
    title: "Trade Compliance Best Practices: Avoiding Costly Mistakes in International Commerce",
    excerpt: "Learn the essential compliance requirements and best practices for smooth import and export operations.",
    category: "Compliance",
    date: "2025-07-10",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["Compliance", "Regulations", "Customs", "Documentation", "Best Practices"],
    content: `
## The Importance of Trade Compliance

Non-compliance with trade regulations can result in:
- Significant financial penalties
- Shipment delays and detention
- Reputational damage
- Loss of trading privileges

## Key Compliance Areas

### 1. Customs Regulations

#### Import Compliance
- Accurate tariff classification
- Correct valuation methods
- Complete documentation
- Duty payment accuracy

#### Export Compliance
- Export licensing requirements
- Sanctions screening
- Dual-use goods controls
- End-user verification

### 2. Product Standards

#### Australian Requirements
- Australian Standards (AS)
- Therapeutic Goods Administration (TGA)
- Australian Quarantine and Inspection Service (AQIS)

#### International Standards
- ISO certifications
- Industry-specific standards
- Country-specific requirements

### 3. Documentation Accuracy

#### Critical Documents
- Commercial Invoice: Must accurately reflect transaction details
- Certificate of Origin: Determines tariff treatment
- Packing List: Essential for customs inspection
- Bill of Lading: Title document for goods

## Common Compliance Mistakes

1. **Incorrect tariff classification** - Can result in duty underpayment or overpayment
2. **Incomplete documentation** - Causes delays and additional scrutiny
3. **Sanctions violations** - Serious legal and financial consequences
4. **Origin declaration errors** - May invalidate FTA benefits

## Best Practices

### Establish Internal Controls
- Regular compliance audits
- Staff training programmes
- Documentation checklists
- Escalation procedures

### Work with Experts
- Licensed customs brokers
- Trade compliance consultants
- Legal specialists when needed

### Stay Informed
- Monitor regulatory changes
- Subscribe to government updates
- Participate in industry associations

## Technology Solutions

Modern compliance tools offer:
- Automated screening
- Document management
- Audit trail maintenance
- Real-time regulatory updates

## Conclusion

Proactive compliance management protects your business and enables smoother international trade operations.
    `
  },
  {
    id: 5,
    slug: "southeast-asia-trade-opportunities",
    title: "Southeast Asia: Australia's Fastest Growing Trade Partner Region",
    excerpt: "Discover why Southeast Asian markets present compelling opportunities for Australian importers and exporters.",
    category: "Market Insights",
    date: "2025-07-01",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["Southeast Asia", "ASEAN", "Export", "Import", "Market Entry"],
    content: `
## The ASEAN Opportunity

Southeast Asia represents one of the world's most dynamic economic regions, with a combined GDP exceeding $3.6 trillion and a population of over 680 million.

## Key Markets

### Singapore
- Regional trading hub
- Gateway to ASEAN
- Advanced logistics infrastructure

### Indonesia
- Largest ASEAN economy
- Growing middle class
- Significant import demand

### Vietnam
- Manufacturing powerhouse
- Rapid economic growth
- Increasing consumer market

### Thailand
- Established manufacturing base
- Agricultural export partner
- Tourism and services hub

### Malaysia
- Diverse economy
- Halal market leader
- Strong trade infrastructure

## Trade Agreement Benefits

The ASEAN-Australia-New Zealand Free Trade Agreement (AANZFTA) provides:
- Preferential tariff rates
- Services market access
- Investment protections
- Simplified rules of origin

## Opportunities by Sector

### Agriculture and Food
- Premium food products
- Dairy and meat
- Wine and beverages
- Health foods

### Resources and Energy
- Minerals and metals
- LNG
- Renewable energy equipment

### Education and Services
- Professional services
- Education exports
- Tourism partnerships

## Challenges to Consider

- Diverse regulatory environments
- Cultural differences
- Infrastructure variations
- Competition from regional suppliers

## Success Strategies

1. **Start with market research** - Understand specific country requirements
2. **Find local partners** - Navigate regulations and culture
3. **Be patient** - Relationship building takes time
4. **Adapt products** - Meet local preferences and standards

## Naviton Global's Southeast Asia Expertise

We provide:
- Market entry planning
- Partner identification
- Compliance guidance
- Ongoing trade support
    `
  },
  {
    id: 6,
    slug: "agricultural-exports-australia",
    title: "Australian Agricultural Exports: Quality, Standards, and Global Demand",
    excerpt: "How Australia's reputation for quality agricultural products creates export opportunities worldwide.",
    category: "Trade Guide",
    date: "2025-06-20",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&q=80",
    author: "Naviton Global Team",
    tags: ["Agriculture", "Export", "Food Safety", "Quality", "Standards"],
    content: `
## Australia's Agricultural Advantage

Australia is renowned globally for:
- Clean, green production systems
- Stringent biosecurity controls
- High food safety standards
- Traceability throughout the supply chain

## Key Export Commodities

### Grains and Pulses
- Wheat: Major export to Asia and Middle East
- Barley: Premium feed and malting grades
- Chickpeas and Lentils: Strong demand in South Asia

### Meat and Livestock
- Beef: Grass-fed premium product
- Lamb: World's largest exporter
- Live cattle: Key Middle East and Southeast Asia markets

### Dairy
- Milk powder and infant formula
- Cheese and butter
- Strong demand from China

### Wine
- Premium wine regions
- Growing Asian markets
- Quality reputation

## Export Requirements

### Documentation
- Phytosanitary certificates
- Health certificates
- Export permits
- Country-specific requirements

### Quality Standards
- AQIS compliance
- HACCP certification
- Organic certification (where applicable)

## Market Opportunities

### China
- Growing middle class demand
- Premium product positioning
- ChAFTA benefits

### Japan
- Long-standing trade partner
- Quality focus
- JAEPA advantages

### Middle East
- Halal certification requirements
- Food security priorities
- Growing populations

### India
- Pulse imports
- Dairy ingredients
- ECTA opportunities

## Challenges

- Biosecurity requirements
- Market access negotiations
- Competition from other exporters
- Currency fluctuations

## Success Factors

1. **Quality consistency** - Maintain standards
2. **Relationship building** - Long-term partnerships
3. **Market adaptation** - Meet specific requirements
4. **Documentation excellence** - Avoid delays

## How We Help

Naviton Global supports agricultural exporters with:
- Market intelligence
- Documentation handling
- Compliance management
- Logistics coordination
    `
  }
];

export const faqs = [
  {
    question: "How do I place an import or export order?",
    answer: "Contact us through our enquiry form or email with your product requirements, origin/destination country, and estimated quantities. Our team will provide a detailed quotation including pricing, shipping options, and timeline within 48 business hours."
  },
  {
    question: "Which countries do you trade with?",
    answer: "We facilitate import and export activities with over 50 countries across Asia, the Middle East, Europe, Africa, and the Americas. Our primary focus markets include India, Southeast Asia, the Middle East, China, Japan, and Korea. We're continuously expanding our global reach."
  },
  {
    question: "Do you handle compliance and documentation?",
    answer: "Yes, we provide comprehensive documentation and compliance support for both imports and exports including certificates of origin, phytosanitary certificates, fumigation certificates, commercial invoices, packing lists, and all customs-required documentation."
  },
  {
    question: "Can you source products from overseas for import?",
    answer: "Absolutely. Our sourcing team can locate and verify suppliers globally for specific products based on your requirements. We handle everything from product specification to quality verification, compliance checks, and delivery coordination."
  },
  {
    question: "What are your minimum order quantities?",
    answer: "Minimum order quantities vary by product category and trade direction. Generally, we work with full container loads (FCL) for optimal pricing, but we can accommodate smaller quantities for certain products. Contact us for specific requirements."
  },
  {
    question: "How do you ensure product quality?",
    answer: "We implement rigorous quality control at multiple stages: supplier verification, pre-shipment inspection, third-party quality audits when required, and compliance with all relevant Australian and international standards for both imports and exports."
  },
  {
    question: "What payment terms do you offer?",
    answer: "We offer flexible payment terms including Letters of Credit (L/C), Telegraphic Transfer (T/T), and Documentary Collections. Specific terms are negotiated based on order value and customer relationship."
  },
  {
    question: "How long does shipping typically take?",
    answer: "Transit times vary by origin/destination: Southeast Asia (7-14 days), India (14-21 days), Middle East (21-28 days), China (10-18 days). Air freight options are available for time-sensitive shipments in either direction."
  }
];

export const aboutData = {
  overview: "Naviton Global Pty Ltd is an Australian international trade company supporting import and export activities between Australia and global markets. Based in Sydney, we work with suppliers and buyers across Australia to deliver reliable, transparent, and commercially focused trade outcomes.",
  vision: "To be the most trusted bridge between Australia and global markets, enabling sustainable growth through reliable import and export solutions for our partners worldwide.",
  mission: "To deliver efficient, compliant, and transparent trade solutions that create lasting value for Australian businesses seeking global markets and international partners seeking Australian quality.",
  values: [
    { title: "Integrity", description: "Transparent dealings and ethical business practices in every transaction." },
    { title: "Compliance", description: "Unwavering commitment to regulatory requirements and industry standards." },
    { title: "Reliability", description: "Consistent delivery on promises with attention to detail and deadlines." },
    { title: "Partnership", description: "Building long-term relationships based on mutual success and trust." }
  ],
  whyAustralia: "Australia offers world-renowned quality, strict regulatory standards, and a reputation for clean, safe products. Our geographic position provides efficient access to Asian markets, while free trade agreements with major economies create competitive advantages for both importers and exporters."
};

export const contactInfo = {
  email: "info@navitonglobal.com.au",
  phone: "+61 478822581",
  address: "Sydney, Australia",
  businessHours: "Monday - Friday: 9:00 AM - 6:00 PM AEST"
};
